import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Printer, X, ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import * as dpService from 'features/dp/services/dpService';
import HoleriteConteudo from './HoleriteConteudo';

/**
 * HoleriteModal – modal that shows the holerite for each employee in a folha,
 * with < > navigation between employees.
 *
 * Props:
 *   folhaId            – id of the current folha
 *   onFechar           – callback to close the modal
 *   funcionarioInicial – optional id to start on a specific employee
 */
export default function HoleriteModal({ folhaId, onFechar, funcionarioInicial }) {
  const [funcionarios, setFuncionarios] = useState([]);
  const [indice, setIndice] = useState(0);
  const [holerite, setHolerite] = useState(null);
  const [carregandoInicial, setCarregandoInicial] = useState(true);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  // Load employee list on mount
  const carregarHolerite = useCallback(async (fId, funcId) => {
    try {
      setCarregando(true);
      setErro(null);
      const resp = await dpService.getHolerite(fId, funcId);
      setHolerite(resp.data?.data ?? null);
    } catch {
      setHolerite(null);
      setErro('Este funcionário não possui itens nesta folha.');
    } finally {
      setCarregando(false);
      setCarregandoInicial(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setCarregandoInicial(true);
        const resp = await dpService.listarFuncionarios({ status: 'ativo', per_page: 100 });
        const raw = resp.data?.data;
        const lista = raw?.data ?? raw ?? [];
        setFuncionarios(lista);

        if (lista.length > 0) {
          let idx = funcionarioInicial
            ? lista.findIndex((f) => f.id === funcionarioInicial)
            : 0;
          if (idx < 0) idx = 0;
          setIndice(idx);
          carregarHolerite(folhaId, lista[idx].id);
        } else {
          setCarregandoInicial(false);
        }
      } catch {
        setErro('Não foi possível carregar os funcionários.');
        setCarregandoInicial(false);
      }
    })();
  }, [carregarHolerite, folhaId, funcionarioInicial]);

  const navegar = (delta) => {
    const novo = indice + delta;
    if (novo < 0 || novo >= funcionarios.length) return;
    setIndice(novo);
    setErro(null);
    carregarHolerite(folhaId, funcionarios[novo].id);
  };

  const funcAtual = funcionarios[indice];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-[color:var(--surface)] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* ── Header ── */}
        <div className="flex items-center justify-between p-5 border-b border-[color:var(--border)]">
          <div className="flex items-center gap-3">
            <div className="bg-teal-100 dark:bg-teal-900/30 p-2 rounded-xl">
              <FileText size={18} className="text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[color:var(--text)]">Holerite</h3>
              <p className="text-xs text-[color:var(--text-muted)]">
                {holerite?.folha?.competencia
                  ? holerite.folha.competencia.split('-').reverse().join('/')
                  : '—'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="p-2 rounded-xl hover:bg-[color:var(--surface-muted)] transition-colors text-[color:var(--text-muted)]"
            >
              <Printer size={16} />
            </button>
            <button
              onClick={onFechar}
              className="p-2 rounded-xl hover:bg-[color:var(--surface-muted)] transition-colors text-[color:var(--text-muted)]"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Employee navigation bar ── */}
        {funcionarios.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--border)] bg-[color:var(--surface-muted)]">
            <button
              onClick={() => navegar(-1)}
              disabled={indice === 0}
              className="p-1 rounded-lg hover:bg-[color:var(--surface)] disabled:opacity-30 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>

            <div className="text-center">
              <div className="text-sm font-medium text-[color:var(--text)]">
                {funcAtual?.pessoa?.nome ?? funcAtual?.nome ?? '—'}
              </div>
              <div className="text-xs text-[color:var(--text-muted)]">
                Matrícula: {funcAtual?.matricula ?? '—'} &nbsp;&middot;&nbsp; {indice + 1} de{' '}
                {funcionarios.length}
              </div>
            </div>

            <button
              onClick={() => navegar(1)}
              disabled={indice === funcionarios.length - 1}
              className="p-1 rounded-lg hover:bg-[color:var(--surface)] disabled:opacity-30 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto p-5">
          {carregandoInicial || carregando ? (
            <div className="flex justify-center py-12">
              <Loader2 size={28} className="animate-spin text-teal-600" />
            </div>
          ) : erro ? (
            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl text-sm">
              <AlertCircle size={15} />
              {erro}
            </div>
          ) : holerite ? (
            <HoleriteConteudo holerite={holerite} />
          ) : (
            <div className="text-center py-12 text-[color:var(--text-muted)]">
              <p className="text-sm">
                Nenhum dado encontrado para este funcionário nesta folha.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
